/**
 * Which column in a company's stock sheet means what.
 *
 * The same shape as `sheetMapping.ts` — a suggested map the company confirms,
 * never a silent guess — but its own field list. A receivables sheet and a
 * stock sheet do not share a vocabulary: one has customers and ageing, the
 * other has items and quantity, and folding them into one `SheetField` union
 * would make an unrelated column mapped by accident the moment the two lists
 * happened to share a word.
 */
import type { FieldRequirement } from './sheetMapping.js';
export type { FieldRequirement };
import { headerKey } from './sheetMapping.js';
export { headerKey };

export type StockField =
    | 'code'
    | 'name'
    | 'category'
    | 'unit'
    | 'quantity'
    | 'rate'
    | 'value'
    | 'minLevel'
    | 'maxLevel'
    | 'lastReceivedOn'
    | 'status'
    | 'note';

export interface StockFieldDefinition {
    field: StockField;
    label: string;
    requirement: FieldRequirement;
    lostWithout: string;
}

/**
 * One required field and no more. A quantity with nothing to call it is not a
 * stock list; everything else improves the screen and none of it should stop
 * a company connecting a sheet that simply lacks a rate column, or a status
 * column, or both.
 */
export const STOCK_FIELDS: readonly StockFieldDefinition[] = [
    { field: 'name', label: 'Item name', requirement: 'required', lostWithout: 'An item has to be called something.' },

    { field: 'quantity', label: 'Quantity', requirement: 'recommended', lostWithout: 'Nothing on the screen says how much there is.' },
    { field: 'code', label: 'Item code', requirement: 'recommended', lostWithout: 'Items are matched by name instead, so two items with the same name become one on the screen.' },
    { field: 'category', label: 'Category / brand', requirement: 'recommended', lostWithout: 'No grouping and no category filter.' },
    { field: 'rate', label: 'Rate', requirement: 'optional', lostWithout: 'No price shown to anyone, whatever their role.' },
    { field: 'value', label: 'Value', requirement: 'optional', lostWithout: 'Calculated from quantity and rate instead, when both are mapped.' },
    { field: 'unit', label: 'Unit', requirement: 'optional', lostWithout: 'Quantity is shown as a bare number.' },
    { field: 'minLevel', label: 'Minimum level', requirement: 'optional', lostWithout: 'Nothing marks an item as running low.' },
    { field: 'maxLevel', label: 'Maximum level', requirement: 'optional', lostWithout: 'The level bar has a floor and no ceiling.' },
    { field: 'lastReceivedOn', label: 'Last received', requirement: 'optional', lostWithout: '' },
    { field: 'status', label: 'Status', requirement: 'optional', lostWithout: '' },
    { field: 'note', label: 'Note', requirement: 'optional', lostWithout: '' },
];

export const STOCK_REQUIRED_FIELDS: readonly StockField[] =
    STOCK_FIELDS.filter(f => f.requirement === 'required').map(f => f.field);

/**
 * The two fields that carry money, and the only ones the server ever redacts.
 * `stockMapping.ts` and the redaction logic in `liveStock.ts` reading from the
 * same list is deliberate — the column a company maps as "Rate" is exactly the
 * column blanked for someone without `stock.view.prices`, never a header the
 * server guesses at independently.
 */
export const STOCK_PRICE_FIELDS: readonly StockField[] = ['rate', 'value'];

export const stockFieldDefinition = (field: StockField): StockFieldDefinition | undefined =>
    STOCK_FIELDS.find(f => f.field === field);

/** field → the exact header text in this company's sheet. */
export type StockColumnMap = Partial<Record<StockField, string>>;

const STOCK_HEADER_RULES: { field: StockField | null; any: string[] }[] = [
    { field: 'code', any: ['itemcode', 'productcode', 'sku', 'partno', 'partnumber', 'stockcode', 'code'] },
    { field: 'category', any: ['category', 'brand', 'subcategory', 'group', 'department'] },

    // Money and quantity before the generic "unit" rule: a compound header
    // like "Rate/Unit" or "Qty per Unit" contains the word "unit" too, and the
    // more consequential field — especially rate, which is what gets redacted
    // — must claim it first.
    { field: 'quantity', any: ['actualquantity', 'quantityonhand', 'stockqty', 'stockquantity', 'quantity', 'qty', 'stock'] },
    { field: 'minLevel', any: ['minlevel', 'minqty', 'minstock', 'reorderlevel', 'min'] },
    { field: 'maxLevel', any: ['maxlevel', 'maxqty', 'maxstock', 'max'] },
    { field: 'rate', any: ['latestrate', 'rate', 'unitprice', 'price', 'costprice', 'cost'] },
    { field: 'value', any: ['stockvalue', 'totalvalue', 'value', 'amount'] },

    { field: 'unit', any: ['unit', 'uom', 'packing'] },

    { field: 'lastReceivedOn', any: ['lastreceived', 'lastreceipt', 'lastin', 'receiveddate'] },
    { field: 'status', any: ['status', 'stockstatus'] },
    { field: 'note', any: ['note', 'remark', 'remarks', 'comment'] },

    // Last: the item's own name, the most generic thing on the sheet.
    { field: 'name', any: ['itemname', 'productname', 'description', 'item', 'product', 'name'] },
];

/** A first guess at the map, for the company to correct. */
export function suggestStockMapping(headers: readonly string[]): StockColumnMap {
    const map: StockColumnMap = {};
    const taken = new Set<string>();

    for (const raw of headers) {
        const key = headerKey(raw);
        if (!key || taken.has(raw)) continue;

        const rule = STOCK_HEADER_RULES.find(r => r.any.some(name => key.includes(name)));
        if (!rule || !rule.field) continue;
        if (map[rule.field] !== undefined) continue;

        map[rule.field] = raw;
        taken.add(raw);
    }

    return map;
}

export type StockMappingProblemKind = 'missing-required' | 'unknown-header' | 'duplicate-column';

export interface StockMappingProblem {
    kind: StockMappingProblemKind;
    field?: StockField;
    header?: string;
    message: string;
}

export function stockMappingProblems(
    headers: readonly string[], map: StockColumnMap,
): StockMappingProblem[] {
    const problems: StockMappingProblem[] = [];
    const present = new Set(headers);
    const usedBy = new Map<string, StockField[]>();

    for (const field of STOCK_REQUIRED_FIELDS) {
        if (!map[field]) {
            problems.push({
                kind: 'missing-required', field,
                message: `${stockFieldDefinition(field)?.label ?? field} has no column chosen.`,
            });
        }
    }

    for (const [field, header] of Object.entries(map) as [StockField, string][]) {
        if (!header) continue;
        if (!present.has(header)) {
            problems.push({
                kind: 'unknown-header', field, header,
                message: `The column “${header}” is not in this sheet any more.`,
            });
        }
        const list = usedBy.get(header);
        if (list) list.push(field); else usedBy.set(header, [field]);
    }

    for (const [header, fields] of usedBy) {
        if (fields.length > 1) {
            problems.push({
                kind: 'duplicate-column', header,
                message: `The column “${header}” is used for ${fields.length} different fields.`,
            });
        }
    }

    return problems;
}

export const stockMappingIsUsable = (headers: readonly string[], map: StockColumnMap): boolean =>
    stockMappingProblems(headers, map).length === 0;
